import { Injectable } from "@angular/core";
import * as imageCompression from "browser-image-compression";
import { fabric } from "fabric";
import {
  Canvas,
  ICanvasOptions,
  IObjectOptions,
  IText,
} from "fabric/fabric-impl";
import { DrawingItemTypeEnum } from "../shared/Models/enums/DrawingItemTypeEnum";
import { TagAssetEnum } from "../shared/Models/enums/TagAssetEnum";
import { PhotoObjectDto } from "../shared/Models/write/PhotoObjectDto";
import { PictureData } from "./canvas/canvas.component";
import { FabricExtensions } from "./fabricExtensionsClass";

const OBJECT_TYPE = "_objectType";
const OBJECT_ID = "_objectId";
const SEQUENCE = "_sequence";
const PHOTO_NAME = "__mainPhoto";
const TRANSPARENT = "#00000001";
const YELLOW = "#ff0";

@Injectable({
  providedIn: "root",
})
export class FabricImageService {
  private propertiesToInclude: string[] = [
    "centeredRotation",
    "selectable",
    "_objectType",
    "aCoords",
    "_objectName",
    "_objectId",
    "_sequence",
  ];

  private canvas: Canvas;
  private originalImg: Blob | string;
  undo_stack: string[] = [];
  redo_stack: string[] = [];

  get Canvas(): Canvas {
    return this.canvas;
  }

  get Layers(): fabric.Object[] {
    if (this.canvas)
      return this.canvas._objects
        .filter((x) => x[OBJECT_TYPE] >= 0)
        .sort((x, y) => (x[SEQUENCE] > y[SEQUENCE] ? 1 : -1));

    return [];
  }

  get redoEnabled(): boolean {
    return this.redo_stack.length > 0;
  }

  get undoEnabled(): boolean {
    return this.undo_stack.length > 1;
  }

  init(
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    options: ICanvasOptions = null
  ) {
    const canvasOptions = options || {
      hoverCursor: "pointer",
      selection: true,
      selectionBorderColor: "blue",
      selectionLineWidth: 1000,
      targetFindTolerance: 15,
    };
    this.canvas = new fabric.Canvas(canvas, canvasOptions);
    this.canvas.setWidth(width);
    this.canvas.setHeight(height);
    this.canvas.selection = false;

    this.canvas.on("object:modified", (event) =>
      event.target[OBJECT_TYPE] >= 0 ? this.saveState() : undefined
    );
    const objectOptions: IObjectOptions = {
      selectable: true,
      cornerSize: 45,
      borderScaleFactor: 3,
      padding: 15,
      cornerStyle: "circle",
      transparentCorners: false,
      perPixelTargetFind: true,
      hasControls: true,
      hasBorders: false,
      lockScalingFlip: true,
      minScaleLimit: 0.1,
    };
    fabric.Object.prototype.setOptions(objectOptions);
    fabric.Object.prototype.setControlsVisibility({
      mtr: false,
      bl: false,
      br: false,
      tl: false,
      tr: false,
      mt: false,
      mb: false,
      mr: false,
      ml: false,
    });
    FabricExtensions.extendFabric();
    fabric.Object.prototype["customizeCornerIcons"]({
      tl: {
        icon: "assets/expand_right_left_24dp.svg",
      },
      tr: {
        icon: "assets/expand_left_right_24dp.svg",
      },
      ml: { icon: "assets/expand_horizontal.svg" },
      mr: { icon: "assets/expand_horizontal.svg" },
      br: {
        icon: "assets/expand_right_left_24dp.svg",
      },
      bl: {
        icon: "assets/expand_left_right_24dp.svg",
      },
      mtr: {
        icon: "assets/loop_black_24dp.svg",
      },
    });
  }

  addBackgroundImage(src: File | string, onloadHandler?: () => void) {
    var url = "";
    if (src instanceof Blob) var url = URL.createObjectURL(src);
    else url = src;
    fabric.Image.fromURL(
      url,
      (image) => {
        image.set({
          hasRotatingPoint: false,
          selectable: false,
          lockMovementX: true,
          lockMovementY: true,
          lockRotation: false,
          lockScalingFlip: true,
          lockScalingX: true,
          lockScalingY: true,
          lockSkewingX: true,
          lockSkewingY: true,
          lockUniScaling: true,
          evented: false,
          _objectName: PHOTO_NAME,
        } as any);

        this.originalImg = src;
        this.canvas.add(image);
        this.canvas.setHeight(image.height);
        this.canvas.setWidth(image.width);
        image.center();
        onloadHandler();
        this.canvas.requestRenderAll();
      },
      { crossOrigin: "Anonymous" }
    );
  }
  scaleToScreen(heightOffset = 0, widthOffset = 0) {
    const obj = new fabric.Object({
      width: this.canvas.getWidth(),
      height: this.canvas.getHeight(),
      minScaleLimit: 0,
    });

    var resizeCalculate: fabric.Object;
    let windowHeight = window.innerHeight - heightOffset;
    let windowWidth = window.innerWidth - widthOffset;
    resizeCalculate = obj.scaleToHeight(windowHeight);
    if (
      windowHeight <
        Math.floor(resizeCalculate.height * resizeCalculate.scaleY) ||
      windowWidth < Math.floor(resizeCalculate.width * resizeCalculate.scaleX)
    )
      resizeCalculate = obj.scaleToWidth(windowWidth);
    this.canvas.setHeight(resizeCalculate.height * resizeCalculate.scaleY);
    this.canvas.setWidth(resizeCalculate.width * resizeCalculate.scaleX);
    this.canvas.forEachObject((obj) => {
      obj.left *= resizeCalculate.scaleX;
      obj.top *= resizeCalculate.scaleY;
      if (obj["_objectType"] == DrawingItemTypeEnum.DrawingArrowLine) {
        var line = obj as fabric.Line;
        var center = line.getCenterPoint();
        var len = Math.sqrt(
          Math.pow((line.x2 - line.x1) * resizeCalculate.scaleX, 2) +
            Math.pow((line.y2 - line.y1) * resizeCalculate.scaleY, 2)
        );
        line.set({
          x1: center.x - len / 2,
          x2: center.x + len / 2,
          y1: center.y,
          y2: center.y,
        });
      } else if (obj["_objectType"] == DrawingItemTypeEnum.Circle) {
        var circle = obj as fabric.Circle;
        circle.set("radius", circle.radius * resizeCalculate.scaleX);
      } else {
        obj.scaleX *= resizeCalculate.scaleX;
        obj.scaleY *= resizeCalculate.scaleY;
      }
      obj.setCoords();
    });
    this.canvas.requestRenderAll();
  }

  async addItems(
    items: PhotoObjectDto[],
    imageBaseUrl: string,
    onDeselect: any,
    onSelectDrawingArrowLineFunction: any,
    onSelectTextboxFunction: any,
    onSelectDrawingFunction: any,
    onSelectTagFunction: any,
    onSelectCircleFunction: any
  ) {
    for (let item of items) {
      const data = JSON.parse(item.metadata);
      const photoObj = this.getPhotoObject();
      switch (data.objectType) {
        case DrawingItemTypeEnum.Line:
        case DrawingItemTypeEnum.Arrow:
        case DrawingItemTypeEnum.Tag:
          let url = `${imageBaseUrl}${DrawingItemTypeEnum[data.objectType]}_${
            TagAssetEnum[data.objectId]
          }.svg`;
          await new Promise<void>((resolve) =>
            fabric.Image.fromURL(url, (image) => {
              const options: IObjectOptions = {
                hasRotatingPoint: data.hasRotatingPoint,
                left: data._newEditor ? data.x * photoObj.scaleX : data.x,
                top: data._newEditor ? data.y * photoObj.scaleY : data.y,
                height: data.height,
                width: data.width,
                angle: data.angle,
                selectable: data.selectable,
                visible: true,
                scaleX: data._newEditor
                  ? data.scaleX * photoObj.scaleX
                  : data.scaleX,
                scaleY: data._newEditor
                  ? data.scaleY * photoObj.scaleY
                  : data.scaleY,
                flipX: data.flipX,
                flipY: data.flipY,
                cornerStyle: "circle",
                backgroundColor: data.backgroundColor,
              };
              image.on("selected", () => onSelectTagFunction());
              image.on("deselected", () => onDeselect());
              this.createImage(
                image,
                options,
                data.objectId,
                data.objectType,
                data.name,
                data._sequence,
                false
              );
              resolve();
            })
          );
          break;

        case DrawingItemTypeEnum.Drawing:
          if (data._newEditor) {
            const group = new fabric.Group([], {
              hasBorders: true,
              hasRotatingPoint: false,
            });
            group.on("selected", () => onSelectDrawingFunction());
            group.on("deselected", () => onDeselect());
            data.items.forEach((obj) => {
              this.drawLine(
                group,
                obj.x * this.canvas.getWidth() + 2.5,
                obj.y * this.canvas.getHeight() + 2.5,
                data._sequence,
                data.color,
                data.strokeWidth,
                data.strokeDashArray
              );
            });
          } else {
            const group = new fabric.Group([], {
              selectable: false,
              hasBorders: true,
              hasRotatingPoint: false,
            });
            group.on("selected", () => onSelectDrawingFunction());
            group.on("deselected", () => onDeselect());
            data.items.forEach((obj) => {
              this.drawLine(group, obj.x, obj.y, data._sequence);
            });
          }
          break;
        case DrawingItemTypeEnum.DrawingArrowLine:
          this.drawArrowLine(
            onDeselect,
            onSelectDrawingArrowLineFunction,
            data.x1 * this.canvas.getWidth(),
            data.y1 * this.canvas.getHeight(),
            data.x2 * this.canvas.getWidth(),
            data.y2 * this.canvas.getHeight(),
            data.color,
            data.strokeWidth,
            data.arrowType,
            data.strokeDashArray
          );
          break;
        case DrawingItemTypeEnum.Text:
          this.createTextBox(
            onDeselect,
            onSelectTextboxFunction,
            data.left * this.canvas.getWidth(),
            data.top * this.canvas.getHeight(),
            data.text,
            data.fill,
            data.borderColor,
            data.backgroundColor,
            data.fontFamily,
            data.fontSize,
            data.fontStyle,
            data.angle,
            data.scaleX,
            data.scaleY
          );
          break;
        case DrawingItemTypeEnum.Circle:
          this.drawCircle(
            onDeselect,
            onSelectCircleFunction,
            data.x * this.canvas.getWidth(),
            data.y * this.canvas.getHeight(),
            data.radius * photoObj.scaleX,
            data.fill,
            data.stroke,
            data.strokeWidth,
            data.strokeDashArray
          );
          break;
      }
    }
    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();
  }

  addImage(
    url: string,
    id: number,
    type: DrawingItemTypeEnum,
    backgroundColor: string,
    name: string,
    onDeselect: any,
    onSelectTagFunction: any
  ) {
    fabric.Image.fromURL(url, (image) => {
      const options: IObjectOptions = {
        top: this.canvas.getHeight() / 2 - image.height / 2,
        left: this.canvas.getWidth() / 2 - image.width / 2,
        selectable: true,
        cornerSize: 50,
        lockScalingY: false,
        borderScaleFactor: 3,
        cornerStyle: "circle",
        backgroundColor,
      };
      name = this.getObjectname(name, type, id);
      image.on("selected", () => onSelectTagFunction());
      image.on("deselected", () => onDeselect());
      this.createImage(
        image,
        options,
        id,
        type,
        name,
        this.canvas._objects.length,
        false
      );
    });
  }

  private createImage(
    image: fabric.Image,
    options: fabric.IObjectOptions,
    id: number,
    type: DrawingItemTypeEnum,
    name: string,
    sequence: number,
    selectObject: boolean
  ) {
    if (!options.backgroundColor) options.backgroundColor = TRANSPARENT;
    image.set(options);
    Object.assign(image, {
      _objectId: id,
      _objectType: type,
      _objectName: name,
      _sequence: sequence,
    });
    this.canvas.add(image);
    if (selectObject) this.canvas.setActiveObject(image);

    this.saveState();
  }

  calcNormalizedPoint(x: number, y: number, transformMatrix?: any[]) {
    const photoObj = this.getPhotoObject();
    const canvasCenter = new fabric.Point(
      this.canvas.getWidth() / 2,
      this.canvas.getHeight() / 2
    );
    const dimensions = new fabric.Point(
      this.canvas.getWidth(),
      this.canvas.getHeight()
    );
    const angle = (360 - this.normalizedAngle(photoObj.angle)) % 360;
    const newDimensions = this.calculateNewDimensions(angle);
    const horizontalDiff = (newDimensions.width - dimensions.x) / 2;
    const verticalDiff = (newDimensions.height - dimensions.y) / 2;
    const point = new fabric.Point(x, y);
    const transformed = transformMatrix
      ? fabric.util.transformPoint(point, transformMatrix)
      : point;
    const rotated = fabric.util.rotatePoint(
      transformed,
      canvasCenter,
      fabric.util.degreesToRadians(angle)
    );
    return new fabric.Point(
      (rotated.x + horizontalDiff) / newDimensions.width,
      (rotated.y + verticalDiff) / newDimensions.height
    );
  }
  normalizedAngle(angle: number) {
    return (360 + angle) % 360;
  }

  private getItems() {
    let objects = [];
    this.canvas.getObjects().forEach((obj: any, i: number) => {
      const metadata: any = {
        objectType: obj._objectType,
        objectId: obj._objectId,
        name: obj._objectName,
        _sequence: i,
      };
      let photoObj = this.getPhotoObject();
      switch (obj._objectType) {
        case DrawingItemTypeEnum.Line:
        case DrawingItemTypeEnum.Arrow:
        case DrawingItemTypeEnum.Tag:
          Object.assign(metadata, {
            _newEditor: true,
            x: obj.left / photoObj.scaleX,
            y: obj.top / photoObj.scaleY,
            height: obj.height,
            width: obj.width,
            angle: obj.angle,
            visible: obj.visible,
            selectable: obj.selectable,
            hasRotatingPoint: obj.hasRotatingPoint,
            scaleX: obj.scaleX / photoObj.scaleX,
            scaleY: obj.scaleY / photoObj.scaleY,
            flipX: obj.flipX,
            flipY: obj.flipY,
            cornerSize: 20,
            cornerStyle: "circle",
            cornerColor: "purple",
            backgroundColor: obj.backgroundColor,
          });
          break;
        case DrawingItemTypeEnum.Drawing:
          if (!obj._objects?.length) return;
          var line = obj._objects.find((o) => o.type != "circle");
          Object.assign(metadata, {
            _newEditor: true,
            color: line.stroke,
            strokeWidth: line.strokeWidth,
            strokeDashArray: line.strokeDashArray,
            items: [],
          });
          var transformMatrix = obj.calcTransformMatrix();
          obj._objects.forEach((item) => {
            if (item.type == "circle") {
              var point = this.calcNormalizedPoint(
                item.left,
                item.top,
                transformMatrix
              );
              metadata.items.push({
                x: point.x,
                y: point.y,
              });
            }
          });
          break;
        case DrawingItemTypeEnum.DrawingArrowLine:
          var points = obj.calcPoints();
          const point1 = this.calcNormalizedPoint(points.x1, points.y1);
          const point2 = this.calcNormalizedPoint(points.x2, points.y2);
          Object.assign(metadata, {
            selectable: obj.selectable,
            hasRotatingPoint: obj.hasRotatingPoint,
            x1: point1.x,
            y1: point1.y,
            x2: point2.x,
            y2: point2.y,
            color: obj.stroke,
            strokeWidth: obj.strokeWidth,
            arrowType: obj.arrowType,
            strokeDashArray: obj.strokeDashArray,
          });
          break;
        case DrawingItemTypeEnum.Text:
          var point = this.calcNormalizedPoint(obj.left, obj.top);
          Object.assign(metadata, {
            left: point.x,
            top: point.y,
            text: obj.text,
            fill: obj.fill,
            backgroundColor: obj.backgroundColor,
            borderColor: obj.cornerColor,
            fontFamily: obj.fontFamily,
            fontSize: obj.fontSize,
            fontStyle: obj.fontStyle,
            angle: this.normalizedAngle(obj.angle - photoObj.angle),
            scaleX: obj.scaleX / photoObj.scaleX,
            scaleY: obj.scaleY / photoObj.scaleY,
          });
          break;
        case DrawingItemTypeEnum.Circle:
          var center = obj.getCenterPoint();
          var point = this.calcNormalizedPoint(center.x, center.y);
          Object.assign(metadata, {
            x: point.x,
            y: point.y,
            stroke: obj.stroke,
            strokeWidth: obj.strokeWidth,
            strokeDashArray: obj.strokeDashArray,
            radius: obj.radius / photoObj.scaleX,
            fill: obj.fill,
          });
          break;
      }
      if (metadata.objectType)
        objects.push({ metadata: JSON.stringify(metadata) });
    });
    return objects;
  }

  getCanvasBlob(
    canvas: HTMLCanvasElement = this.canvas.getElement()
  ): Promise<File> {
    return imageCompression.default.canvasToFile(
      canvas,
      "image/jpeg",
      "modified",
      Date.now()
    );
  }

  async getPictureData(): Promise<PictureData> {
    this.canvas
      .setBackgroundImage(null, this.canvas.renderAll.bind(this.canvas))
      .setBackgroundColor(null, this.canvas.renderAll.bind(this.canvas))
      .discardActiveObject()
      .renderAll();
    if (this.originalImg instanceof Blob)
      return {
        originalBlob: this.originalImg,
        blob: await this.getCanvasBlob(),
        items: this.getItems(),
        backgroundImageMetadata: this.getBackgroundImageMetadata(),
      } as PictureData;

    return {
      originalUrl: this.originalImg,
      blob: await this.getCanvasBlob(),
      items: this.getItems(),
      backgroundImageMetadata: this.getBackgroundImageMetadata(),
    } as PictureData;
  }

  toggleItemsSelection(selectable: boolean) {
    this.canvas.forEachObject((obj) => {
      if (
        obj[OBJECT_TYPE] == DrawingItemTypeEnum.Line ||
        obj[OBJECT_TYPE] == DrawingItemTypeEnum.Arrow ||
        obj[OBJECT_TYPE] == DrawingItemTypeEnum.Tag
      )
        obj.selectable = selectable;
    });
    if (!selectable) this.canvas.discardActiveObject().requestRenderAll();
  }

  private getPhotoObject() {
    return this.canvas
      .getObjects()
      .find((obj: any) => obj._objectName == PHOTO_NAME);
  }

  private calculateNewDimensions(degrees: number) {
    const canvasCenter = new fabric.Point(
      this.canvas.getWidth() / 2,
      this.canvas.getHeight() / 2
    );
    const image = this.getPhotoObject();
    const tlCorner = image.aCoords.tl.clone();
    const newTlCorner = fabric.util.rotatePoint(
      tlCorner,
      canvasCenter,
      fabric.util.degreesToRadians(degrees)
    );
    const blCorner = image.aCoords.bl.clone();
    const newBlCorner = fabric.util.rotatePoint(
      blCorner,
      canvasCenter,
      fabric.util.degreesToRadians(degrees)
    );

    const tlWidth = Math.abs(newTlCorner.x - canvasCenter.x) * 2;
    const tlHeight = Math.abs(newTlCorner.y - canvasCenter.y) * 2;
    const blWidth = Math.abs(newBlCorner.x - canvasCenter.x) * 2;
    const blHeight = Math.abs(newBlCorner.y - canvasCenter.y) * 2;

    return {
      width: Math.max(tlWidth, blWidth),
      height: Math.max(tlHeight, blHeight),
    };
  }

  rotate(degrees: number) {
    if (degrees == 0) return;
    const canvasCenter = new fabric.Point(
      this.canvas.getWidth() / 2,
      this.canvas.getHeight() / 2
    );
    const dimensions = new fabric.Point(
      this.canvas.getWidth(),
      this.canvas.getHeight()
    );
    this.canvas.setDimensions(this.calculateNewDimensions(degrees));
    const horizontalDiff = (this.canvas.getWidth() - dimensions.x) / 2;
    const verticalDiff = (this.canvas.getHeight() - dimensions.y) / 2;
    const radians = fabric.util.degreesToRadians(degrees);
    this.canvas.forEachObject((obj) => {
      const objectOrigin = new fabric.Point(obj.left, obj.top);
      const newPos = fabric.util.rotatePoint(
        objectOrigin,
        canvasCenter,
        radians
      );
      obj.angle = this.normalizedAngle(obj.angle + degrees);
      obj.top = newPos.y + verticalDiff;
      obj.left = newPos.x + horizontalDiff;
      obj.setCoords();
    });
    this.canvas.requestRenderAll();
    this.saveState();
  }

  flip(horizontally: boolean) {
    if (horizontally)
      this.canvas.forEachObject((obj) => {
        obj.flipX = !obj.flipX;
      });
    else
      this.canvas.forEachObject((obj) => {
        obj.flipY = !obj.flipY;
      });

    this.canvas.requestRenderAll();
  }

  setZoom(zoom: number) {
    this.canvas.zoomToPoint(
      new fabric.Point(this.canvas.getWidth() / 2, this.canvas.getHeight() / 2),
      zoom
    );
    this.canvas.requestRenderAll();
  }

  zoom(zoomIn: boolean) {
    const zoomValue = this.canvas.getZoom() + (zoomIn ? 0.05 : -0.05);
    this.setZoom(zoomValue);
  }

  redrawBaseLineGroup(
    strokeColor?: string,
    strokeWidth?: number,
    arrowType?: number,
    strokeDashArray?: number[]
  ) {
    var group = this.getDrawingGroup();
    var tempObjects = group?._objects?.filter((x) => x["x1"] != null);
    if (tempObjects?.length > 0) {
      group.set({ dirty: true, objectCaching: false });
      tempObjects.forEach((tempObject) => {
        tempObject.set({
          dirty: true,
          objectCaching: false,
          stroke: strokeColor ?? tempObject.stroke,
          strokeWidth: strokeWidth ?? tempObject.strokeWidth,
          strokeDashArray: strokeDashArray ?? tempObject.strokeDashArray,
        });
        tempObject["arrowType"] = arrowType ?? tempObject["arrowType"] ?? 3;
        this.canvas.renderAll();
      });
      this.saveState();
    }
  }

  updateArrowLineOptions(
    strokeColor?: string,
    strokeWidth?: number,
    arrowType?: number,
    strokeDashArray?: number[]
  ) {
    var tempObject = this.canvas.getActiveObject();
    if (tempObject) {
      tempObject["arrowType"] = arrowType ?? tempObject["arrowType"] ?? 3;
      var options = {
        stroke: strokeColor ?? tempObject.stroke,
        strokeWidth: strokeWidth ?? tempObject.strokeWidth,
        strokeDashArray: strokeDashArray ?? tempObject.strokeDashArray,
        dirty: true,
        objectCaching: false,
      };
      tempObject.setOptions(options);
      this.canvas.renderAll();
      this.saveState();
    }
  }

  drawArrowLine(
    onDeselect: any,
    onSelect: any,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: string,
    strokeWidth: number,
    arrowType: number,
    strokeDashArray: number[],
    setControls: boolean = true
  ) {
    const centerPoint = new fabric.Point((x1 + x2) / 2, (y1 + y2) / 2);
    const length = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    const point1 = new fabric.Point(centerPoint.x - length / 2, centerPoint.y);
    const point2 = new fabric.Point(centerPoint.x + length / 2, centerPoint.y);
    let xDiff = x2 - x1;
    let yDiff = y2 - y1;
    let angle = this.normalizedAngle(
      fabric.util.radiansToDegrees(Math.atan2(yDiff, xDiff))
    );
    var arrow = new fabric["LineArrow"](
      [point1.x, point1.y, point2.x, point2.y],
      {
        _objectType: DrawingItemTypeEnum.DrawingArrowLine,
        _objectName: this.getObjectname(
          "DrawingArrowLine ",
          DrawingItemTypeEnum.DrawingArrowLine
        ),
        centeredRotation: true,
        originX: "center",
        originY: "center",
        fill: color,
        stroke: color,
        strokeWidth: strokeWidth,
        selectable: true,
        arrowType: arrowType,
        strokeDashArray: strokeDashArray,
      }
    );
    arrow.on("selected", () => onSelect(setControls));
    arrow.on("deselected", () => onDeselect());
    this.canvas.add(arrow);
    arrow.rotate(angle);
    arrow.setCoords();
    this.saveState();
    this.canvas.requestRenderAll();
    return arrow;
  }

  drawCircle(
    onDeselect: any,
    onSelect: any,
    x: number,
    y: number,
    radius: number,
    fill: string,
    color: string,
    strokeWidth: number,
    strokeDashArray: number[]
  ) {
    var options = {
      _objectType: DrawingItemTypeEnum.Circle,
      _objectName: this.getObjectname("Circle ", DrawingItemTypeEnum.Circle),
      selectable: true,
      originX: "center",
      originY: "center",
      hasRotatingPoint: false,
      top: y,
      left: x,
      radius,
      stroke: color,
      fill,
      strokeWidth,
      strokeDashArray,
    };
    var circle = new fabric.Circle(options);
    circle.on("selected", () => onSelect());
    circle.on("deselected", () => onDeselect());
    this.canvas.add(circle);
    this.saveState();
    this.canvas.requestRenderAll();
    return circle;
  }

  getDrawingGroup(): fabric.Group {
    return this.canvas._objects.filter(
      (x) => x?.["_objectType"] == DrawingItemTypeEnum.Drawing
    )[0] as fabric.Group;
  }

  drawLine(
    group: fabric.Group,
    x: number,
    y: number,
    sequence: number,
    color?: string,
    strokeWidth?: number,
    strokeDashArray?: number[]
  ) {
    var drawingGroup = this.getDrawingGroup();
    if (!drawingGroup) {
      Object.assign(group, {
        _objectType: DrawingItemTypeEnum.Drawing,
        _objectName: this.getObjectname(
          "Drawing ",
          DrawingItemTypeEnum.Drawing
        ),
        _sequence: sequence || this.canvas._objects.length,
      });
      this.canvas.add(group);
    } else {
      const coords = drawingGroup._objects[
        drawingGroup._objects.length - 1
      ].aCoords.tl.add(drawingGroup.getCenterPoint());
      drawingGroup.addWithUpdate(
        new fabric.Line([coords.x + 2.5, coords.y + 2.5, x, y], {
          fill: color ?? YELLOW,
          stroke: color ?? YELLOW,
          strokeWidth: strokeWidth ?? 1,
          strokeDashArray: strokeDashArray ?? [0, 0],
          selectable: false,
        })
      );
      this.saveState();
    }
    group.addWithUpdate(
      new fabric.Circle({
        left: x - 2.5,
        top: y - 2.5,
        strokeWidth: 1,
        radius: 0,
        hasBorders: false,
        hasControls: false,
        selectable: false,
      })
    );
    this.canvas.requestRenderAll();
  }

  saveState() {
    this.undo_stack.push(this.canvas.toJSON(this.propertiesToInclude));
  }

  clearState() {
    this.undo_stack = [];
    this.redo_stack = [];
  }

  clearCanvas() {
    var allObjects = this.canvas.getObjects();
    allObjects.shift();
    this.canvas.remove(...allObjects);
    this.saveState();
  }

  getBackgroundImageMetadata() {
    var backgroundImage = this.canvas.getObjects()[0];
    const metadata = {
      angle: backgroundImage.angle,
      flipX: backgroundImage.flipX,
      flipY: backgroundImage.flipY,
      zoom: this.canvas.getZoom(),
    };
    return JSON.stringify(metadata);
  }

  get lastState() {
    return this.undo_stack[this.undo_stack.length - 1];
  }

  undo() {
    let state = this.undo_stack.pop();
    this.redo_stack.push(state);
    this.canvas.loadFromJSON(
      this.lastState,
      this.canvas.renderAll.bind(this.canvas)
    );
  }

  redo() {
    if (this.redo_stack.length == 0) return;

    let state = this.redo_stack.pop();
    if (state != null) {
      this.undo_stack.push(state);
      this.canvas.loadFromJSON(state, this.canvas.renderAll);
    }
  }

  createTextBox(
    onDeselect: any,
    onSelectTextboxFunction: any,
    x: number,
    y: number,
    text: string,
    color: string,
    borderColor: string,
    backgroundColor: string,
    font: string = "Times New Roman",
    size: number = 40,
    style: "" | "normal" | "italic" | "oblique" = "",
    angle: number = 0,
    scaleX: number = 1,
    scaleY: number = 1
  ) {
    const photoObj = this.getPhotoObject();
    const options = {
      _objectType: DrawingItemTypeEnum.Text,
      _objectName: this.getObjectname("Textbox ", DrawingItemTypeEnum.Text),
      left: x,
      top: y,
      fill: color,
      color: color,
      backgroundColor: backgroundColor,
      cornerColor: borderColor,
      fontFamily: font,
      fontSize: size,
      fontStyle: style,
      angle: angle,
      hasBorders: false,
      padding: 5,
      scaleX: scaleX * photoObj.scaleX,
      scaleY: scaleY * photoObj.scaleY,
      textAlign: "center",
      editable: false,
      dirty: true,
    };
    var textBox = FabricExtensions.createOverwrittenITextClass(text, options);
    textBox.on("selected", () => onSelectTextboxFunction());
    textBox.on("deselected", () => onDeselect());
    this.canvas.add(textBox);
    this.saveState();
  }

  private getLargestDimension(useScaling: boolean = false) {
    let photoObj = this.getPhotoObject();
    if (photoObj.width >= photoObj.height)
      return useScaling ? photoObj.width / photoObj.scaleX : photoObj.width;
    else
      return useScaling ? photoObj.height / photoObj.scaleY : photoObj.height;
  }

  // TEXTBOX
  addTextBox(
    onDeselect: any,
    onSelect: any,
    text: string,
    color: string,
    backgroundColor: string,
    borderColor: string,
    setActive: boolean = false
  ) {
    const photoObj = this.getPhotoObject();
    const options = {
      _objectType: DrawingItemTypeEnum.Text,
      _objectName: this.getObjectname("Textbox ", DrawingItemTypeEnum.Text),
      top: 100,
      left: 100,
      fill: color,
      color: color,
      backgroundColor: backgroundColor,
      cornerColor: borderColor,
      scaleX: photoObj.scaleX,
      scaleY: photoObj.scaleY,
      hasBorders: false,
      padding: 5,
      fontSize: this.getLargestDimension(false) / 20,
      textAlign: "center",
      editable: false,
      dirty: true,
    };
    var textBox = FabricExtensions.createOverwrittenITextClass(text, options);
    textBox.on("selected", () => onSelect());
    textBox.on("deselected", () => onDeselect());
    this.canvas.add(textBox);
    this.saveState();
    if (setActive) this.canvas.setActiveObject(textBox);
  }
  updateTextBoxText(text: string) {
    var obj = this.getActiveObject() as IText;
    obj.set({
      text: text,
      dirty: true,
    });
    this.canvas.renderAll();
    this.saveState();
  }

  setTextColor(color: string) {
    var t = this.canvas.getActiveObject() as fabric.IText;
    if (t) {
      t.fill = color;
      t.dirty = true;
      // t.setColor(color);
      this.canvas.renderAll();
      this.saveState();
    }
  }
  setTextBorderColor(color: string) {
    var t = this.canvas.getActiveObject();
    if (t) {
      t.setOptions({ cornerColor: color, dirty: true });
      this.canvas.renderAll();
      this.saveState();
    }
  }
  setTextBackgroundColor(color: string) {
    var t = this.canvas.getActiveObject();
    if (t) {
      t.setOptions({ backgroundColor: color, dirty: true });
      this.canvas.renderAll();
      this.saveState();
    }
  }

  updateLineColor(color: string) {
    var line = this.canvas.getActiveObject();
    line.fill = color;
    line.stroke = color;
    line.setOptions({
      fill: color,
      stroke: color,
    });
    this.saveState();
  }
  getActiveObject(): fabric.Object {
    return this.canvas.getActiveObject();
  }
  setActiveObject(object: fabric.Object) {
    this.canvas.setActiveObject(object);
  }
  removeActiveObject(): void {
    var obj = this.canvas.getActiveObject();
    if (obj) this.removeObject(obj);
  }
  removeObject(object: any): void {
    this.canvas.remove(object);
    this.canvas.renderAll();
    this.saveState();
  }
  deselectActiveObject(): void {
    var obj = this.canvas.getActiveObject();
    if (obj != null) {
      this.canvas.discardActiveObject();
      this.canvas.renderAll();
    }
  }

  copyAndPasteActiveObject(
    onDeselect: any,
    onSelectDrawingArrowLineFunction: any,
    onSelectTextboxFunction: any,
    onSelectTagFunction: any,
    onSelectCircleFunction: any
  ) {
    var obj = this.canvas.getActiveObject();
    switch (obj["_objectType"]) {
      case DrawingItemTypeEnum.Drawing:
        // "SHOULD NOT BE ALLOWED AS DRAWING IS 1 GROUP NOT MORE";
        break;
      case DrawingItemTypeEnum.Tag:
        obj.clone((clonedObject) => {
          this.pasteObject(
            onDeselect,
            onSelectTagFunction,
            clonedObject,
            DrawingItemTypeEnum.Tag,
            obj["_objectName"].replace(/[0-9]/g, ""),
            obj["_objectId"]
          );
        }, this.propertiesToInclude);
        break;
      case DrawingItemTypeEnum.Text:
        var color = obj["fill"] as string;
        var borderColor = obj["cornerColor"];
        var text = obj["text"];
        this.canvas.discardActiveObject();
        this.addTextBox(
          onDeselect,
          onSelectTextboxFunction,
          text,
          color,
          obj.backgroundColor,
          borderColor,
          true
        );
        break;
      case DrawingItemTypeEnum.DrawingArrowLine:
        obj.clone(
          (clonedObject) => {
            this.pasteArrowObject(
              onDeselect,
              onSelectDrawingArrowLineFunction,
              clonedObject,
              obj
            );
          },
          [...this.propertiesToInclude, "arrowType"]
        );
        break;
      case DrawingItemTypeEnum.Circle:
        obj.clone((clonedObject) => {
          clonedObject.hasRotatingPoint = obj.hasRotatingPoint;
          this.pasteObject(
            onDeselect,
            onSelectCircleFunction,
            clonedObject,
            DrawingItemTypeEnum.Circle,
            "Circle "
          );
        }, this.propertiesToInclude);
      case DrawingItemTypeEnum.Line:
      case DrawingItemTypeEnum.Arrow:
      default:
        break;
    }
  }

  private pasteObject(
    onDeselect: any,
    onSelect: any,
    object: fabric.Object,
    objectType: DrawingItemTypeEnum,
    objectName: string,
    id?: number
  ) {
    if (object != null) {
      this.canvas.discardActiveObject();
      object.set({
        left: object.left + 10,
        top: object.top + 10,
        evented: true,
      });
      object["_objectType"] = objectType;
      object["_objectName"] = this.getObjectname(objectName, objectType, id);
      object["_sequence"] = this.canvas._objects.length;
      object.on("selected", () => onSelect());
      object.on("deselected", () => onDeselect());
      this.canvas.add(object);
      this.canvas.setActiveObject(object);
      this.canvas.requestRenderAll();
      this.saveState();
    }
  }

  private pasteArrowObject(
    onDeselect: any,
    onSelect: any,
    clonedObject: any,
    obj: any
  ) {
    if (clonedObject != null) {
      clonedObject.arrowType = obj.arrowType;
      clonedObject.x1 = obj.x1 + 10;
      clonedObject.y1 = obj.y1 + 10;
      clonedObject.x2 = obj.x2 + 10;
      clonedObject.y2 = obj.y2 + 10;
      this.canvas.discardActiveObject();
      clonedObject.set({
        left: clonedObject.left + 10,
        top: clonedObject.top + 10,
        evented: true,
      });
      clonedObject._objectName = this.getObjectname(
        "DrawingArrowLine ",
        DrawingItemTypeEnum.DrawingArrowLine
      );
      clonedObject._objectType = obj._objectType;
      clonedObject.on("selected", () => onSelect());
      clonedObject.on("deselected", () => onDeselect());
      this.canvas.add(clonedObject);
      this.canvas.setActiveObject(clonedObject);
      this.canvas.requestRenderAll();
      this.saveState();
    }
  }
  private getObjectname(
    prefix: string,
    drawingTypeEnum: DrawingItemTypeEnum,
    id?: number
  ) {
    return (
      prefix +
      (this.canvas._objects.filter(
        (x) =>
          x[OBJECT_TYPE] == drawingTypeEnum &&
          (drawingTypeEnum == DrawingItemTypeEnum.Tag
            ? x[OBJECT_ID] == id
            : true)
      ).length +
        1)
    );
  }
}
